import Auth from '@aws-amplify/auth';
import axios from 'axios';
import axiosRetry from 'axios-retry';

/**
 * Class to send server requests uses axios, stores the Security Tokens.
 * Make it accesisble for all child classes
 */
export class BaseSender {
  static currentCity = null;

  static tokenTypes = {
    ACCESS_TOKEN: 'accessToken',
    ID_TOKEN: 'idToken',
    REFRESH_TOKEN: 'refreshToken',
  };

  /**
   * Method to set the currentcity across all childs.
   * @param {} currentCity
   */
  static setCurrentCity(currentCity) {
    BaseSender.currentCity = currentCity;
    this.dataDomain = `${this.baseDomain}/${currentCity}`;
  }

  constructor() {
    this.axiosInstance = axios.create({
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      timeout: 900000, // https://medium.com/@masnun/handling-timeout-in-axios-479269d83c68
    });

    axiosRetry(this.axiosInstance, {
      retries: 5,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
    });
  }

  axiosInstance = null;

  generateEscapedUri(path) {
    const rawUri = `${this.getDataDomain()}${path}`;
    return encodeURI(rawUri);
  }

  generateEscapedUriNoCity(path) {
    const rawUri = `${this.getDataDomainNoCity()}${path}`;
    return encodeURI(rawUri);
  }

  /**
   * Function to get the authorization tokens from the current session
   */
  async getTokens() {
    // eslint-disable-line class-methods-use-this
    // getting the current session will refresh the token
    const cognitoUserSession = await Auth.currentSession();
    const accessToken =
      cognitoUserSession[BaseSender.tokenTypes.ACCESS_TOKEN].jwtToken;
    const idToken = cognitoUserSession[BaseSender.tokenTypes.ID_TOKEN].jwtToken;
    return {
      authorization: accessToken,
      xAmzSecurityToken: `Bearer ${idToken}`,
    };
  }

  async setToken() {
    const tokens = await this.getTokens();
    this.axiosInstance.defaults.headers.common.Authorization =
      tokens.authorization;
    this.axiosInstance.defaults.headers.common['X-Amz-Security-Token'] =
      tokens.xAmzSecurityToken;
  }

  /**
   * Override these method in subclasses in order to determine the
   * part of the url before the `path`
   */
  // eslint-disable-next-line class-methods-use-this
  getDataDomain() {
    throw new Error('You must override this'); // Override Eslint .eslintclass-methods-use-this
  }

  // eslint-disable-next-line class-methods-use-this
  getDataDomainNoCity() {
    throw new Error('You must override this'); // Override Eslint .eslintclass-methods-use-this
  }

  /**
   * GET requests with BaseSener
   * @param {string} path
   * @param {object} queryParams GET params. E.g.: { id: 12345 } will send <path>?id=12345
   * Check of this.localDataOverride[path] is checking to see if the data for a specific path
   * was set in local-config.js, e.g. { kpi : true }.  In this case, it will then return
   * data from the dummy-data file. Otherwise, it will return data from the server.
   */
  async get(path, queryParams, responseType = 'json', otherParams = {}) {
    // params: query string kv's set by arg of call to .get method
    // mode: query string kv's set in Admin panel by dev
    const queryParamsAndMode = { ...this.getModeKeyValues(), ...queryParams };

    // this.constructor will give static Class. Ensures static method of subclass called if defined
    await this.setToken(path);
    const escapedUrl = this.generateEscapedUri(path);
    return this.axiosInstance.get(escapedUrl, {
      params: queryParamsAndMode,
      responseType,
      ...otherParams,
    });
  }

  // We should switch to using this one
  async getV2(path, params = {}) {
    const { queryParams, ...otherParams } = params;
    const queryParamsAndMode = { ...this.getModeKeyValues(), ...queryParams };
    await this.setToken(path);
    const escapedUrl = this.generateEscapedUri(path);
    return this.axiosInstance.get(escapedUrl, {
      params: queryParamsAndMode,
      ...otherParams,
    });
  }

  // Created for endpoints that doesn't need the city
  async rawGet(path, params = {}) {
    const { queryParams, ...otherParams } = params;
    const queryParamsAndMode = { ...this.getModeKeyValues(), ...queryParams };
    await this.setToken(path);
    const escapedUrl = this.generateEscapedUriNoCity(path);

    return this.axiosInstance.get(escapedUrl, {
      params: queryParamsAndMode,
      ...otherParams,
    });
  }

  async post(path, body, responseType = '') {
    await this.setToken();
    let bodyToSend;
    if (body === undefined) {
      // Send an empty object {} to help ensure Content-Type header is sent. This is important for compatibility with
      // backends like stroracle-api, which explicitly lists 'application/json' in app.api.binary_types (see
      // stroracle-api/app.py), and thus expects this header. It also aligns with Chalice's general behavior for binary
      // types (see https://aws.github.io/chalice/topics/views.html#binary-content). This helps avoid issues where Axios
      // might drop the header with no body (see https://github.com/axios/axios/issues/5814).
      bodyToSend = {};
    } else if (body && body.toJSON) {
      bodyToSend = body.toJSON();
    } else {
      bodyToSend = body;
    }
    const escapedUrl = this.generateEscapedUri(path);
    return this.axiosInstance.post(escapedUrl, bodyToSend, { responseType });
  }

  /**
   * Get current `mode` parameters as a key-value map, e.g. { show_all: true }
   * This is a stub/abstract method to be implemented in sender subclasses.
   * @return {Record<string, any>} Key-value pairs to be included in the query
   * string of API requests. Values must be serializable/usable by Axios.
   */
  // eslint-disable-next-line class-methods-use-this
  getModeKeyValues() {
    return {};
  }
}
